import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserCircle, LogOut, ShieldCheck, Mail } from "lucide-react";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import { useDocMeta } from "../../lib/useDocMeta";

export default function CustomerAccount() {
  useDocMeta({ title: "Account · GovBidIQ", path: "/app/account" });
  const { customer, logout } = useCustomerAuth();
  const nav = useNavigate();

  return (
    <div className="max-w-2xl space-y-6" data-testid="cust-account">
      <header>
        <div className="tick-label text-sky-400 mb-1">/ Account</div>
        <h1 className="font-display font-black text-3xl text-slate-100 inline-flex items-center gap-2">
          <UserCircle className="w-6 h-6 text-sky-400" /> Account
        </h1>
        <p className="text-slate-400 text-sm mt-1">Manage your sign-in details and session.</p>
      </header>

      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 space-y-3">
        <div className="tick-label text-sky-400">/ Profile</div>
        <Row label="Email" value={customer?.email || "—"} icon={Mail} testid="cust-account-email" />
        <Row label="Customer ID" value={customer?.customer_user_id || "—"} icon={ShieldCheck} mono testid="cust-account-id" />
        <p className="text-[11px] text-slate-500 leading-relaxed">
          GovBidIQ uses email-based magic-link sign-in. To change your email, contact support — we&apos;ll migrate your reports.
        </p>
      </section>

      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 space-y-3">
        <div className="tick-label text-sky-400">/ Billing</div>
        <p className="text-sm text-slate-300 leading-relaxed">
          Reports are billed per-purchase via Stripe at checkout. A consolidated billing portal will arrive in a future release.
        </p>
        <Link to="/pricing" data-testid="cust-account-pricing" className="inline-block border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-widest uppercase px-3 py-2">View Pricing & Tiers</Link>
      </section>

      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 space-y-3">
        <div className="tick-label text-sky-400">/ Session</div>
        <p className="text-sm text-slate-400 leading-relaxed">Signing out ends this browser session immediately. You can sign back in any time with a fresh magic link.</p>
        <button onClick={async () => { await logout(); nav("/customer/login"); }} data-testid="cust-account-logout" className="inline-flex items-center gap-1.5 border border-rose-500/60 text-rose-300 hover:bg-rose-500/10 font-mono text-[11px] tracking-widest uppercase px-3 py-2">
          <LogOut className="w-3.5 h-3.5" /> Log out
        </button>
      </section>
    </div>
  );
}

function Row({ label, value, icon: Icon, mono, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-slate-500 mt-1" />
      <div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</div>
        <div className={`text-slate-100 ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</div>
      </div>
    </div>
  );
}
